/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { toast } from "react-toastify";
import { LAYER_TYPE } from "../../../../actions/Layers/constants";
import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import {
    useAllocatedLayers,
    useAvailableRegisterKeys,
    useEndRipeningCycle,
    useFacilityLocation,
    useFacilityLocations,
    useLocationChecks,
    useLocationCheckSessions,
    useLocationPredictions,
    usePatchFacilityLocation,
    usePatchRipeningCycle,
    useRegisterData,
    useRegisterDataSeries,
    useRipeningCycles,
    useRoomEvents,
    useStartRipeningCycle,
    useTodayRegisterData,
} from "../../../../hooks/useFacilityLocationQueries";
import { Alert } from "../../../../types/alerts";
import { getFilledArrayOrDefault } from "../../../../utils";
import { useGroupBy } from "../../../Helper/GroupByModal";

import { useDateRangeQueryParams } from "../../../../hooks/useDateRangeQueryParams";
import { CycleStartTracker, emptyCycleStartTracker, useCycleStartTracker } from "./useCycleStartTracker";

export const LIST_OR_GRID = {
    LIST: "list",
    GRID: "grid"
};


const accentColors = [
    css`border-color: #0d6efd!important;`, // Blue
    css`border-color: #ffc107!important;`, // Yellow
    css`border-color: #6610f2!important;`, // Indigo
    css`border-color: #fd7e14!important;`, // Orange
    css`border-color: #198754!important;`, // Green
    css`border-color: #d63384!important;`, // Pink
    css`border-color: #0dcaf0!important;`, // Cyan
    css`border-color: #dc3545!important;`, // Red
    css`border-color: #20c997!important;`, // Teal
    css`border-color: #6f42c1!important;`, // Purple
];

function coerceFiniteNumber(value: unknown): number | undefined {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length === 0) return undefined;
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}

interface RipeningRoomContextProps {
    refreshRegisterData: () => void;
    refreshRegisterDataSeries: () => void;
    refreshRoomEvents: () => void;
    refreshAllocatedLayers: () => void;
    refreshLocationChecks: () => void;
    refreshCheckSessions: () => void;
    refreshTodayRegisterData: () => void;
    refreshPredictions: () => void;
    refreshRipeningCycles: () => void;
    patchLocation: (field: string, value: any, keepHistory?: boolean, callApi?: boolean) => void;
    startRipeningCycle: (query: any) => Promise<void>;
    startRipeningCycleLoading: boolean;
    // * Follows the program start of the active cycle (starting / started / failed).
    cycleStartTracker: CycleStartTracker;
    endRipeningProgram: (query: any) => void;
    endRipeningProgramLoading: boolean;
    editRipeningCycle: (query: any) => void;
    editRipeningCycleLoading: boolean;
    today: dayjs.Dayjs;
    layers: any[]; // * allocated layers in given timeframe
    groups: any[]; // * Found groups in allocated layers in given timeframe
    pallets: any[]; // * Found pallets in allocated layers in given timeframe, with attached group details
    listOrGrid: string;
    setListOrGrid: React.Dispatch<React.SetStateAction<string>>;
    filterLevel: string | null;
    setFilterLevel: React.Dispatch<React.SetStateAction<string | null>>;
    cellLevels: string[];
    cellRows: string[];
    cellColumns: string[];
    selectedLayers: Record<string, boolean>;
    setSelectedLayers: React.Dispatch<React.SetStateAction<object>>;
    selectedLayersArray: string[];
    deselectAll: () => void;
    groupAccentColors: Record<string, any>;
    fruit_type: string | null;
    fruit_type_config?: any;
    dayViewRegisterKeys: string[];
    dayViewLabelByKey: Record<string, string>;
    sensorGraphConfig: Array<{ key: string; label: string; color?: string; min?: number; max?: number; sma_n?: number }>;
    fruitSensorConfig: Array<{ key: string; label: string; color?: string; min?: number; max?: number; sma_n?: number }>;
    currentLocation: any;
    groupByProps: any;
    todayRegisterData: any;
    roomEvents: any[];
    predictions: any[];
    ripeningCycles: any[];
    activeRipeningCycle: any | null;
    selectedRipeningCycleId: string | null;
    selectedRipeningCycle: any | null;
    viewingActiveCycle: boolean;
    ripeningActionAllowed: boolean;
    allocatedLayersPerDay: any[]; // * For utility functions
    locations: any[]; // * All facility locations
    registerData: any[]; // * Register data for date range
    registerDataSeries: any; // * Register data series for plotting
    availableRegisterKeys: { key: string; label: string }[];
    checks: any[]; // * Location checks for date range
    checkSessions: any[]; // * Check sessions for date range
    allocatedLayersLoading: boolean; // * Loading state for allocated layers
    allocatedLayersError: boolean; // * Error state for allocated layers
    // Alert management
    alerts: Alert[];
    addAlert: (alert: Omit<Alert, "id" | "timestamp">) => void;
    removeAlert: (id: string) => void;
    clearAlerts: () => void;
    clearAlertsBySource: (source: string) => void;
    setSearchParam: (key: string, value: string) => void;
    // Date range state
    fromDateParam: string | null;
    toDateParam: string | null;
    referenceDay: dayjs.Dayjs;
    dateRange: dayjs.Dayjs[];
    goToDateRange: (day: dayjs.Dayjs) => void;
    firstDayInRange: dayjs.Dayjs;
    lastDayInRange: dayjs.Dayjs;
    prev: () => void;
    next: () => void;
    isDateRangePinned: boolean;
}

const RipeningRoomContext = createContext({
    currentLocation: null,
    refreshRegisterData: () => null,
    refreshRegisterDataSeries: () => null,
    refreshRoomEvents: () => null,
    refreshPredictions: () => null,
    refreshRipeningCycles: () => null,
    refreshAllocatedLayers: () => null,
    refreshLocationChecks: () => null,
    refreshCheckSessions: () => null,
    refreshTodayRegisterData: () => null,
    patchLocation: () => null,
    startRipeningCycle: async () => undefined,
    startRipeningCycleLoading: false,
    cycleStartTracker: emptyCycleStartTracker,
    endRipeningProgram: () => null,
    endRipeningProgramLoading: false,
    editRipeningCycle: () => null,
    editRipeningCycleLoading: false,
    today: dayjs(),
    layers: [],
    groups: [],
    pallets: [],
    listOrGrid: LIST_OR_GRID.LIST,
    setListOrGrid: () => null,
    filterLevel: null,
    setFilterLevel: () => null,
    cellLevels: [],
    cellRows: [],
    cellColumns: [],
    selectedLayers: {},
    setSelectedLayers: () => null,
    deselectAll: () => null,
    selectedLayersArray: [],
    groupAccentColors: {},
    fruit_type: null,
    dayViewRegisterKeys: [],
    dayViewLabelByKey: {},
    sensorGraphConfig: [],
    fruitSensorConfig: [],
    groupByProps: {} as any,
    todayRegisterData: null,
    roomEvents: [],
    predictions: [],
    ripeningCycles: [],
    activeRipeningCycle: null,
    selectedRipeningCycleId: null,
    selectedRipeningCycle: null,
    viewingActiveCycle: false,
    ripeningActionAllowed: true,
    allocatedLayersPerDay: [],
    locations: [],
    registerData: [],
    registerDataSeries: null,
    availableRegisterKeys: [],
    checks: [],
    checkSessions: [],
    allocatedLayersLoading: false,
    allocatedLayersError: false,
    // Alert management defaults
    alerts: [],
    addAlert: () => null,
    removeAlert: () => null,
    clearAlerts: () => null,
    clearAlertsBySource: () => null,
    setSearchParam: () => null,
    fromDateParam: null,
    toDateParam: null,
    referenceDay: dayjs(),
    dateRange: [],
    goToDateRange: () => null,
    firstDayInRange: dayjs(),
    lastDayInRange: dayjs(),
    prev: () => null,
    next: () => null,
    isDateRangePinned: false,
} as RipeningRoomContextProps);


dayjs.extend(customParseFormat);

export const DATE_URL_FORMAT = "YYYY-MM-DD";
const DATE_QUERY_FORMAT = "YYYY-MM-DD";
const DATE_DISPLAY_FORMAT = "DD MMM YYYY";
const DEFAULT_RIPENING_SENSOR_KEYS = [
    "co2_value",
    "c2h4_value",
    "o2_value",
    "temperature_sensor_1",
    "temperature_sensor_2",
];

export default function useRipeningRoom() {
    return useContext(RipeningRoomContext);
}

export function RipeningRoomProvider({ children }) {
    const today = dayjs();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const config = useConfig();
    const { location_id } = params;

    const selectedRipeningCycleId = useMemo(
        () => (searchParams.get("ripening_cycle_id") === "none" ? null : (searchParams.get("ripening_cycle_id") || null)),
        [searchParams]
    );

    // Alert management state
    const [alerts, setAlerts] = useState<Alert[]>([]);

    // Alert management functions
    const addAlert = useCallback((alert: Omit<Alert, "id" | "timestamp">) => {
        setAlerts((prev) => {
            // Check if an alert with the same source and message already exists
            const existingAlert = prev.find((existing) => existing.source === alert.source && existing.message === alert.message);

            if (existingAlert) {
                return prev; // Don't add duplicate alert
            }

            const newAlert: Alert = {
                ...alert,
                id: `${alert.source || "unknown"}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                timestamp: new Date(),
            };
            return [...prev, newAlert];
        });
    }, []);

    const removeAlert = useCallback((id: string) => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    }, []);

    const clearAlerts = useCallback(() => {
        setAlerts([]);
    }, []);

    const clearAlertsBySource = useCallback((source: string) => {
        setAlerts((prev) => prev.filter((alert) => alert.source !== source));
    }, []);

    // * TanStack Query hooks for data fetching
    const { data: currentLocation } = useFacilityLocation(location_id, addAlert);
    const { data: _facilityLocations } = useFacilityLocations({ offset: 0, limit: 100 });

    // * Fysical Location Setup
    const [listOrGrid, setListOrGrid] = useState(LIST_OR_GRID.LIST);
    const [filterLevel, setFilterLevel] = useState<string | null>(null);
    const cellLevels = getFilledArrayOrDefault((config.root_config?.ripening_room_levels || "").split(",").map((i) => i.trim()).filter((i) => i), []);
    const cellColumns = getFilledArrayOrDefault((config.root_config?.ripening_room_columns || "").split(",").map((i) => i.trim()).filter((i) => i), []);
    const cellRows = getFilledArrayOrDefault((config.root_config?.ripening_room_rows || "").split(",").map((i) => i.trim()).filter((i) => i), []);


    // * Load date for the week minus 1 day before to also include the pallets that have been moving out of the room
    const {
        fromDateParam,
        toDateParam,
        referenceDay: baseReferenceDay,
        dateRange: baseDateRange,
        goToDateRange,
        firstDayInRange: baseFirstDayInRange,
        lastDayInRange: baseLastDayInRange,
        prev: basePrev,
        next: baseNext,
    } = useDateRangeQueryParams(
        parseInt(config.root_config?.ripening_room_days_before || "2", 10),
        parseInt(config.root_config?.ripening_room_days_after || "2", 10)
    );

    const { data: ripeningCyclesResponse, refetch: refetchRipeningCycles } = useRipeningCycles(location_id, 12, 0);
    const ripeningCycles = ripeningCyclesResponse?.results || [];

    const activeRipeningCycle = useMemo(
        () => ripeningCycles.find((cycle) => cycle?.active) || null,
        [ripeningCycles]
    );
    const selectedRipeningCycle = useMemo(
        () => ripeningCycles.find((cycle) => cycle?.id === selectedRipeningCycleId) || null,
        [ripeningCycles, selectedRipeningCycleId]
    );

    const viewingActiveCycle = useMemo(
        () => !!selectedRipeningCycle && !!activeRipeningCycle && selectedRipeningCycle.id === activeRipeningCycle.id,
        [selectedRipeningCycle, activeRipeningCycle]
    );

    const ripeningActionAllowed = useMemo(
        () => viewingActiveCycle || !selectedRipeningCycle,
        [viewingActiveCycle, selectedRipeningCycle]
    );

    const isDateRangePinned = !!selectedRipeningCycle;

    const pinnedFirstDayInRange = useMemo(() => {
        if (!selectedRipeningCycle) return baseFirstDayInRange;
        return dayjs(selectedRipeningCycle.ripening_start_date);
    }, [selectedRipeningCycle, baseFirstDayInRange]);

    const pinnedLastDayInRange = useMemo(() => {
        if (!selectedRipeningCycle) return baseLastDayInRange;
        return dayjs(selectedRipeningCycle.ripening_end_date);
    }, [selectedRipeningCycle, baseLastDayInRange]);

    const pinnedDateRange = useMemo(() => {
        if (!selectedRipeningCycle) return baseDateRange;
        const start = dayjs(selectedRipeningCycle.ripening_start_date).startOf("day");
        const end = dayjs(selectedRipeningCycle.ripening_end_date).startOf("day");
        const days: dayjs.Dayjs[] = [];
        let cursor = start;
        while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
            days.push(cursor);
            cursor = cursor.add(1, "day");
        }
        return days;
    }, [selectedRipeningCycle, baseDateRange]);

    const referenceDay = isDateRangePinned ? pinnedFirstDayInRange : baseReferenceDay;
    const firstDayInRange = pinnedFirstDayInRange;
    const lastDayInRange = pinnedLastDayInRange;
    const dateRange = pinnedDateRange;

    const prev = useCallback(() => {
        if (isDateRangePinned) {
            return;
        }
        basePrev();
    }, [isDateRangePinned, basePrev]);

    const next = useCallback(() => {
        if (isDateRangePinned) {
            return;
        }
        baseNext();
    }, [isDateRangePinned, baseNext]);

    const queryFromDate = firstDayInRange.subtract(1, "day").format(DATE_QUERY_FORMAT);
    const queryFromDataCheckAndSensorData = firstDayInRange.format(DATE_QUERY_FORMAT);
    const queryToDate = lastDayInRange.format(DATE_QUERY_FORMAT);

    // Day view register keys are now configurable with display_filter per item.
    // We use the *unfiltered* keys for data fetching (before fruit_type is known),
    // and then compute the filtered set later once fruit_type is derived.
    const dayViewConfigRaw = getFilledArrayOrDefault(
        config.root_config?.ripening_room_day_view_register_keys,
        DEFAULT_RIPENING_SENSOR_KEYS.map((key) => ({ key }))
    );
    const dayViewConfigNormalized = (dayViewConfigRaw || []).map((item: any) => (
        typeof item === "string" ? ({ key: item }) : item
    ));
    const dayViewRegisterKeysForQuery = getFilledArrayOrDefault(
        dayViewConfigNormalized
            .map((item: any) => item?.field?.key || item?.key)
            .filter((key: any) => typeof key === "string" && key.length > 0),
        DEFAULT_RIPENING_SENSOR_KEYS
    );

    // * TanStack Query hooks for data fetching dependent on date range
    const { data: allocatedLayersData, refetch: refetchAllocatedLayers, isLoading: allocatedLayersLoading, isError: allocatedLayersError } = useAllocatedLayers(location_id, queryFromDate, queryToDate);
    const { data: _checks, refetch: refetchLocationChecks } = useLocationChecks(location_id, queryFromDataCheckAndSensorData, queryToDate);
    const { data: _checkSessions, refetch: refetchCheckSessions } = useLocationCheckSessions(location_id, queryFromDataCheckAndSensorData, queryToDate);
    const { data: _registerData, refetch: refetchRegisterData } = useRegisterData(location_id, queryFromDate, queryToDate, dayViewRegisterKeysForQuery);
    const { data: todayRegisterData, refetch: refetchTodayRegisterData } = useTodayRegisterData(location_id);
    const { data: availableRegisterKeys } = useAvailableRegisterKeys();
    const { data: roomEvents, refetch: refetchRoomEvents } = useRoomEvents(currentLocation?.client, queryFromDate, addAlert);
    const { data: predictions, refetch: refetchPredictions } = useLocationPredictions(location_id, queryFromDate, queryToDate);

    // * Patch mutation hook
    const patchMutation = usePatchFacilityLocation();

    // * Start ripening program mutation hook
    const startRipeningCycleMutation = useStartRipeningCycle();

    // * End ripening program mutation hook
    const endRipeningProgramMutation = useEndRipeningCycle();

    // * Patch ripening cycle mutation hook
    const patchRipeningCycleMutation = usePatchRipeningCycle();

    // * Extract layers and days data from allocated layers response
    const layers = allocatedLayersData?.layers || [];
    const allocatedLayersPerDay = allocatedLayersData?.days || [];

    // * Util function to refresh data
    const refreshAllocatedLayers = useCallback(() => {
        refetchAllocatedLayers();
    }, [refetchAllocatedLayers]);

    const refreshLocationChecks = useCallback(() => {
        refetchLocationChecks();
    }, [refetchLocationChecks]);

    const refreshCheckSessions = useCallback(() => {
        refetchCheckSessions();
    }, [refetchCheckSessions]);

    const refreshRegisterDataCallback = useCallback(() => {
        refetchRegisterData();
    }, [refetchRegisterData]);

    const refreshTodayRegisterDataCallback = useCallback(() => {
        refetchTodayRegisterData();
    }, [refetchTodayRegisterData]);

    const refreshRoomEvents = useCallback(() => {
        refetchRoomEvents();
    }, [refetchRoomEvents]);

    const refreshPredictions = useCallback(() => {
        refetchPredictions();
    }, [refetchPredictions]);

    const refreshRipeningCycles = useCallback(() => {
        refetchRipeningCycles();
    }, [refetchRipeningCycles]);

    const cycleStartTracker = useCycleStartTracker({
        locationId: location_id,
        location: currentLocation,
        activeCycle: activeRipeningCycle,
        refreshRipeningCycles,
    });
    const { trackCycleStart } = cycleStartTracker;

    const patchLocationCallback = useCallback((field: string, value: any, keepHistory = false, callApi = false) => {
        if (location_id) {
            patchMutation.mutate({ locationId: location_id, field, value, keepHistory, callApi });
        }
    }, [location_id, patchMutation]);

    // * State for selected layers
    const [selectedLayers, setSelectedLayers] = useState({});
    const selectedLayersArray = Object.keys(selectedLayers).filter((key) => selectedLayers[key]);
    const deselectAll = () => setSelectedLayers({});

    const setSearchParam = useCallback((key: string, value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (!value) {
                next.delete(key);
            } else {
                next.set(key, value);
            }
            return next;
        });
    }, [setSearchParams]);

    const endRipeningProgramCallback = useCallback((query: any) => {
        if (!location_id || !activeRipeningCycle) return;

        // Use ripening room fruit type (derived from the query, since the pallets-derived
        // fruit_type is declared later in this hook) to filter the configured form fields.
        const fruitType = query?.fruit_type || null;
        const formFields = applyDisplayFilterFields(config.root_config?.ripening_room_edit_cycle_form || [], { fruit_type: fruitType });

        const { ripening_start_date: startDate, ripening_end_date: endDate, ripening_target: targetClass, season } = query || {};

        const meta = formFields
            .filter((field) => !["ripening_start_date", "ripening_end_date", "ripening_target", "season"].includes(field.name))
            .reduce((acc, field) => {
                const value = query?.[field.name];
                if (value !== null && value !== undefined && value !== "") {
                    acc[field.name] = value;
                }
                return acc;
            }, {});

        if (query.ripening_program) {
            meta.ripening_program = query.ripening_program;
        }
        if (query.ripening_program_override) {
            meta.ripening_program_override = query.ripening_program_override;
        }

        const payload: any = {};
        if (startDate) payload.ripening_start_date = startDate;
        if (endDate) payload.ripening_end_date = endDate;
        if (targetClass !== undefined && targetClass !== null) payload.ripening_target = targetClass;
        if (season !== undefined && season !== null) payload.season = season;
        if (Object.keys(meta).length > 0) payload.meta = meta;

        endRipeningProgramMutation.mutate(
            {
                locationId: location_id,
                cycleId: activeRipeningCycle.id,
                payload,
            },
            {
                onSuccess: () => {
                    refreshRipeningCycles();
                    setSearchParam("ripening_cycle_id", "none");
                },
            }
        );
    }, [location_id, activeRipeningCycle, currentLocation, endRipeningProgramMutation, refreshRipeningCycles, setSearchParam, config.root_config?.ripening_room_edit_cycle_form]);

    // * Get table items for the selected group strategy
    const group_by_fields = applyDisplayFilterFields(config?.root_config?.ripening_group_by_fields);
    const groupByProps = useGroupBy(group_by_fields);

    // * If a cycle is selected, get the layer ids that are atleast one full day allocated to the location
    const allocatedLayerIdsInRange = useMemo(() => {
        if (!selectedRipeningCycle) {
            return null;
        }

        const dateKeys = new Set(dateRange.map((date) => date.format(DATE_URL_FORMAT)));
        const layerIds = new Set<string>();

        allocatedLayersPerDay.forEach((day) => {
            const dayKey = dayjs(day.date).format(DATE_URL_FORMAT);
            if (!dateKeys.has(dayKey)) {
                return;
            }

            (day.layer_ids || []).forEach((layerId) => layerIds.add(layerId));
        });

        return layerIds;
    }, [selectedRipeningCycle, allocatedLayersPerDay, dateRange]);

    const cycleLayers = useMemo(() => {
        if (!selectedRipeningCycle || !allocatedLayerIdsInRange) {
            return layers;
        }
        return layers.filter((layer) => allocatedLayerIdsInRange.has(layer.id));
    }, [layers, selectedRipeningCycle, allocatedLayerIdsInRange]);
    const [groupsOrFalse, pallets] = groupByProps.getGroups(cycleLayers, LAYER_TYPE.PALLET);

    let groups = groupsOrFalse;

    // TODO: consider if this belongs in useGroupBy.getGroups method
    if (!groupsOrFalse) {
        groups = [{
            group_key: "",
            group_label: "",
            items: pallets,
            pallets: pallets.length,
            boxes: pallets.reduce((acc, i) => acc + (i.number_of_boxes || 0), 0)
        }];
    }

    const groupAccentColors = groups.reduce((acc, group, index) => {
        acc[group.group_key] = accentColors[index % accentColors.length];
        return acc;
    }, {});

    // * Rooms normally hold one fruit type. A pallet moved out on the reference day is still listed on that day (it can have checks),
    // * but not on the next day, so take the fruit type from the pallets listed on the day after the reference day.
    const fruit_type = useMemo(() => {
        const nextDayKey = referenceDay.add(1, "day").format(DATE_URL_FORMAT);
        const nextDay = allocatedLayersPerDay.find((day) => dayjs(day.date).format(DATE_URL_FORMAT) === nextDayKey);
        const nextDayIds = new Set<string>(nextDay?.layer_ids || []);
        const palletsNextDay = pallets.filter((p) => nextDayIds.has(p.id));
        const firstFruitType = (list: any[]) => list.find((p) => p.fruit_type)?.fruit_type;

        // * Fall back to all pallets when the next day is not loaded or has no pallets with a fruit type
        return firstFruitType(palletsNextDay) || firstFruitType(pallets) || null;
    }, [pallets, allocatedLayersPerDay, referenceDay]);
    const fruit_type_config = config.get_fruit_type(fruit_type);

    const dayViewConfig = (applyDisplayFilterFields(dayViewConfigNormalized, { fruit_type }) || [])
        .filter((item: any) => item && typeof item.key === "string" && item.key.length > 0);

    const dayViewRegisterKeys = getFilledArrayOrDefault(
        dayViewConfig.map((item: any) => item.key),
        DEFAULT_RIPENING_SENSOR_KEYS
    );

    const dayViewLabelByKey = dayViewConfig.reduce((acc: Record<string, string>, item: any) => {
        if (item?.label && item?.key) {
            acc[item.key] = item.label;
        }
        return acc;
    }, {});

    const labelByRegisterKey = (availableRegisterKeys || []).reduce((acc: Record<string, string>, item) => {
        acc[item.key] = item.label || item.key;
        return acc;
    }, {});

    // Y-axis bounds default to the register configuration (device management screen).
    // A min/max set on the graph config entry overrides it, but only for that graph.
    const boundsByRegisterKey = (availableRegisterKeys || []).reduce((acc: Record<string, { min?: number | null, max?: number | null }>, item) => {
        acc[item.key] = { min: item.min_value, max: item.max_value };
        return acc;
    }, {});

    const resolveBound = (configValue: unknown, registerValue: unknown): number | undefined => (
        coerceFiniteNumber(configValue) ?? coerceFiniteNumber(registerValue)
    );

    const sensorGraphConfigRaw = getFilledArrayOrDefault(
        config.root_config?.ripening_room_sensor_graph_register_keys,
        DEFAULT_RIPENING_SENSOR_KEYS.map((key) => ({ key }))
    );
    const sensorGraphConfig = (applyDisplayFilterFields(sensorGraphConfigRaw || [], { fruit_type }) || [])
        .filter((item: any) => item && typeof item.key === "string" && item.key.length > 0)
        .map((item: any) => ({
            key: item.key,
            label: item.label || labelByRegisterKey[item.key] || item.key,
            color: item.color,
            min: resolveBound(item.min, boundsByRegisterKey[item.key]?.min),
            max: resolveBound(item.max, boundsByRegisterKey[item.key]?.max),
            sma_n: coerceFiniteNumber(item.sma_n),
        }));

    const fruitSensorConfigRaw = getFilledArrayOrDefault(
        config.root_config?.ripening_room_fruit_sensor_graph_register_keys,
        []
    );
    const fruitSensorConfig = (applyDisplayFilterFields(fruitSensorConfigRaw, { fruit_type }) || [])
        .filter((item: any) => item && typeof item.key === "string" && item.key.length > 0)
        .map((item: any) => ({
            key: item.key,
            label: item.label || labelByRegisterKey[item.key] || item.key,
            color: item.color,
            min: resolveBound(item.min, boundsByRegisterKey[item.key]?.min),
            max: resolveBound(item.max, boundsByRegisterKey[item.key]?.max),
            sma_n: coerceFiniteNumber(item.sma_n),
        }));

    // * Filter graph register config based on the room fruit type
    const graphRegisterKeys = sensorGraphConfig.map((i) => i.key);

    const { data: registerDataSeries, refetch: refetchRegisterDataSeries } = useRegisterDataSeries(
        location_id,
        queryFromDataCheckAndSensorData,
        queryToDate,
        graphRegisterKeys,
        selectedRipeningCycle?.ripening_start_date ?? null
    );

    const refreshRegisterDataSeriesCallback = useCallback(() => {
        refetchRegisterDataSeries();
    }, [refetchRegisterDataSeries]);

    const startRipeningCycleCallback = useCallback(async (query) => {
        if (!location_id) return;

        const { ripening_start_date: startDate, ripening_end_date: endDate, ripening_target: targetClass, season } = query;

        // Use ripening room fruit type (derived from first pallet) to filter the configured form fields.
        const fruitType = fruit_type || query.fruit_type || null;
        const formFields = applyDisplayFilterFields(config.root_config?.ripening_room_start_cycle_form || [], { fruit_type: fruitType });

        const meta = formFields
            .filter((field) => !["ripening_start_date", "ripening_end_date", "ripening_target", "season"].includes(field.name))
            .reduce((acc, field) => {
                const value = query[field.name];
                if (value !== null && value !== undefined && value !== "") {
                    acc[field.name] = value;
                }
                return acc;
            }, {});

        if (query.ripening_program) {
            meta.ripening_program = query.ripening_program;
        }
        // * One-time adjustments made in the program preview; the backend applies and
        // * validates them (and re-sends them when the start is tried again).
        if (query.ripening_program_id && query.ripening_program_override) {
            meta.ripening_program_override = query.ripening_program_override;
        }

        if (!startDate || !endDate) {
            toast.error("Please fill in both start and end date before starting a ripening cycle.");
            return;
        }

        await startRipeningCycleMutation.mutateAsync(
            {
                locationId: location_id,
                payload: {
                    ripening_start_date: startDate,
                    ripening_end_date: endDate,
                    ripening_target: targetClass,
                    season,
                    layer_ids: [],
                    meta,
                    ripening_program_id: query.ripening_program_id,
                    ripening_program_fruit_type: query.ripening_program ? fruitType : undefined,
                    idempotency_key: query.idempotency_key,
                    tenant_id: currentLocation?.tenant_id,
                },
            },
            {
                onSuccess: (data) => {
                    refreshRipeningCycles();
                    if (data?.cycle_id) {
                        setSearchParam("ripening_cycle_id", data.cycle_id);
                    }
                    trackCycleStart(data);
                    if (data?.warning) {
                        toast.warning(data.warning, { hideProgressBar: true });
                    }
                },
            }
        );
    }, [location_id, currentLocation?.tenant_id, fruit_type, selectedLayersArray.join(","), startRipeningCycleMutation, refreshRipeningCycles, setSearchParam, trackCycleStart, config.root_config?.ripening_room_start_cycle_form]);

    const editRipeningCycleCallback = useCallback((query: any) => {
        if (!location_id || !activeRipeningCycle) return;

        const { ripening_start_date: startDate, ripening_end_date: endDate, ripening_target: targetClass, season } = query;

        // Use ripening room fruit type (derived from first pallet) to filter the configured form fields.
        const fruitType = fruit_type || query.fruit_type || null;
        const formFields = applyDisplayFilterFields(config.root_config?.ripening_room_edit_cycle_form || [], { fruit_type: fruitType });

        const meta = formFields
            .filter((field) => !["ripening_start_date", "ripening_end_date", "ripening_target", "season"].includes(field.name))
            .reduce((acc, field) => {
                const value = query[field.name];
                if (value !== null && value !== undefined && value !== "") {
                    acc[field.name] = value;
                }
                return acc;
            }, {});

        const payload: any = {};
        if (startDate) payload.ripening_start_date = startDate;
        if (endDate) payload.ripening_end_date = endDate;
        if (targetClass !== undefined && targetClass !== null) payload.ripening_target = targetClass;
        if (season !== undefined && season !== null) payload.season = season;
        if (Object.keys(meta).length > 0) payload.meta = meta;

        patchRipeningCycleMutation.mutate(
            {
                locationId: location_id,
                cycleId: activeRipeningCycle.id,
                payload,
            },
            {
                onSuccess: () => {
                    refreshRipeningCycles();
                },
            }
        );
    }, [location_id, activeRipeningCycle, fruit_type, currentLocation, patchRipeningCycleMutation, refreshRipeningCycles, patchLocationCallback, config.root_config?.ripening_room_edit_cycle_form]);

    // Automatically select the active cycle if one exists and no cycle is currently selected
    useEffect(() => {
        if (activeRipeningCycle && !selectedRipeningCycleId) {
            if (searchParams.get("ripening_cycle_id") === "none") {
                return;
            }
            setSearchParam("ripening_cycle_id", activeRipeningCycle.id);
        }
    }, [activeRipeningCycle, selectedRipeningCycleId, setSearchParam]);

    return <RipeningRoomContext.Provider value={{
        refreshRegisterData: refreshRegisterDataCallback,
        refreshRegisterDataSeries: refreshRegisterDataSeriesCallback,
        refreshTodayRegisterData: refreshTodayRegisterDataCallback,
        refreshRoomEvents,
        refreshPredictions,
        refreshRipeningCycles,
        currentLocation,
        refreshAllocatedLayers,
        refreshLocationChecks,
        refreshCheckSessions,
        patchLocation: patchLocationCallback,
        startRipeningCycle: startRipeningCycleCallback,
        startRipeningCycleLoading: startRipeningCycleMutation.isPending,
        cycleStartTracker,
        endRipeningProgram: endRipeningProgramCallback,
        endRipeningProgramLoading: endRipeningProgramMutation.isPending,
        editRipeningCycle: editRipeningCycleCallback,
        editRipeningCycleLoading: patchRipeningCycleMutation.isPending,
        today,
        layers: Array.isArray(layers) ? layers : [],
        groups,
        pallets,
        listOrGrid,
        setListOrGrid,
        filterLevel,
        setFilterLevel,
        cellLevels,
        cellRows,
        cellColumns,
        selectedLayers,
        setSelectedLayers,
        selectedLayersArray,
        deselectAll,
        groupAccentColors,
        fruit_type,
        fruit_type_config,
        dayViewRegisterKeys,
        dayViewLabelByKey,
        sensorGraphConfig,
        fruitSensorConfig,
        groupByProps,
        todayRegisterData,
        roomEvents: roomEvents || [],
        predictions: predictions || [],
        ripeningCycles,
        activeRipeningCycle,
        selectedRipeningCycleId,
        selectedRipeningCycle,
        viewingActiveCycle,
        ripeningActionAllowed,
        allocatedLayersPerDay,
        locations: _facilityLocations || [],
        registerData: _registerData || [],
        registerDataSeries: registerDataSeries || null,
        availableRegisterKeys: availableRegisterKeys || [],
        checks: _checks || [],
        checkSessions: _checkSessions || [],
        allocatedLayersLoading,
        allocatedLayersError,
        // Alert management
        alerts,
        addAlert,
        removeAlert,
        clearAlerts,
        clearAlertsBySource,
        setSearchParam,
        fromDateParam,
        toDateParam,
        referenceDay,
        dateRange,
        goToDateRange,
        firstDayInRange,
        lastDayInRange,
        prev,
        next,
        isDateRangePinned,
    }} >
        {children}
    </RipeningRoomContext.Provider>;
}

// Utility to format date as 'DD MMM YYYY'
export const formatDate = (date) => (date ? date.format(DATE_DISPLAY_FORMAT) : "");

/**
 * Hook that returns a callback function to check if a specific layer (pallet/po) is allocated
 * to the ripening room on a given date.
 *
 * @returns A callback function `(layer_id: string, date: dayjs.Dayjs) => boolean`
 *          Returns `true` if the layer is allocated on the specified date, `false` otherwise.
 *
 * @example
 * const isItemAllocatedOnDate = useIsItemAllocated();
 * const isAllocated = isItemAllocatedOnDate(palletId, today);
 * // Use to enable/disable checkboxes or buttons for specific pallets
 */
export function useIsItemAllocated() {
    const { allocatedLayersPerDay } = useContext(RipeningRoomContext);
    return useCallback((layer_id, date) => {
        const allocatedLayers = allocatedLayersPerDay.find((day) => dayjs(day.date).format(DATE_URL_FORMAT) === date.format(DATE_URL_FORMAT));
        if (!allocatedLayers) {
            return false;
        }
        return allocatedLayers.layer_ids.includes(layer_id);
    }, [allocatedLayersPerDay]);
}

/**
 * Hook that returns a callback function to get all layers (pallets/po) that are allocated
 * to the ripening room on a given date, optionally filtered by layer type.
 *
 * @returns A callback function `(date: dayjs.Dayjs, layer_type?: string | null) => Layer[]`
 *          Returns an array of layer objects allocated on the specified date.
 *          If `layer_type` is provided (e.g., "PALLET", "BOX"), only layers of that type are returned.
 *
 * @example
 * const getAllocatedLayers = useGetAllocatedLayers();
 * const allPallets = getAllocatedLayers(today, "PALLET");
 * const allLayers = getAllocatedLayers(today); // Returns all types
 * // Use to display lists or counts of allocated items for a specific date
 */
export function useGetAllocatedLayers() {
    const { allocatedLayersPerDay, layers } = useContext(RipeningRoomContext);
    return useCallback((date, layer_type: string | null = null) => {
        const allocatedLayers = allocatedLayersPerDay.find((day) => dayjs(day.date).format(DATE_URL_FORMAT) === date.format(DATE_URL_FORMAT));
        // * All the layers filtered by the given date
        const date_layers = layers.filter((i) => allocatedLayers?.layer_ids.includes(i.id));

        if (!layer_type) {
            return date_layers;
        }
        // * Filter the layers by the given type
        return date_layers.filter((layer) => layer.type === layer_type);
    }, [allocatedLayersPerDay, layers]);
}
